import { defineStore, storeToRefs } from 'pinia';
import { ref, computed } from 'vue';
import { startCase, isEmpty } from 'lodash-es';
import { useAppLoading } from '@/stores/app/loading';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useUsage } from '@/stores/insights/usage';
import { useActionableInsights } from '@/stores/insights/actionableInsights';
import { useDemographicSurveyReport } from '@/stores/insights/demographicSurveyReport';
import { getSafeError } from '@/stores/insights/helpers';
import { useAppRoute } from '@/stores/app/route';
import { useWeeklySnapshot } from '@/stores/insights/weeklySnapshot';
import { useContentSnapshot } from '@/stores/insights/contentSnapshot';
import { useLanguageSnapshot } from '@/stores/insights/languageSnapshot';
import { useGenderReport } from '@/stores/insights/genderReport';
import { usePassthroughDropoffReport } from '@/stores/insights/passthroughDropoffReport';
import { useFilters } from '@/stores/filters/filters';
import { useHiringReport } from '@/stores/insights/hiringReport';
import { useOverallReport } from '@/stores/insights/overallReport';
import { useOutboundReport } from '@/stores/insights/outboundReport';
import { useInboundReport } from '@/stores/insights/inboundReport';
import { useExecutiveInsights } from '@/stores/insights/executiveInsights';
import { useRecruitingPerformance } from './recruitingPerformance';

let navigatedToDefaultExternalPageViaHistory = false;

// Triggers on back/forward button press
window.addEventListener('popstate', () => {
  const defaultExternalPages = [
    '/app/insights/compliance-report',
    '/app/insights/employer-brand-report',
    '/app/insights/job-performance-report',
    '/app/insights/usage',
  ];

  if (defaultExternalPages.includes(window.location.pathname)) {
    navigatedToDefaultExternalPageViaHistory = true;
  }
});

export const useInsights = defineStore('insights', () => {
  // State
  const labelsForLoading = ref({});
  const reportingMessage = ref({});
  const emailStatus = ref({
    succeeded: {
      type: 'success',
      message: 'One or more emails have been sent successfully',
      tooltipMessage: 'Emails sent to:',
      items: [],
    },
    failed: {
      type: 'error',
      message: 'One or more emails failed to send',
      tooltipMessage: 'Failed to send to:',
      items: [],
    },
  });
  const showInternalDefault = ref(true);

  // Getters
  const emptyData = computed(() => {
    const routeStore = useAppRoute();
    const { route } = storeToRefs(routeStore);

    if (route.value?.name === 'recruiting-performance') {
      const recruitingPerformanceStore = useRecruitingPerformance();
      const { summary } = storeToRefs(recruitingPerformanceStore);
      const { table = [] } = summary.value;

      return !table.length;
    }

    if (route.value?.name === 'weekly-snapshot') {
      const weeklySnapshotStore = useWeeklySnapshot();
      const { documents } = storeToRefs(weeklySnapshotStore);

      return !documents.value?.length;
    }

    if (route.value?.name === 'content-snapshot') {
      const contentSnapshotStore = useContentSnapshot();
      const { contentStats } = storeToRefs(contentSnapshotStore);

      return !Object.keys(contentStats.value || {}).length;
    }

    if (route.value?.name === 'language-snapshot') {
      const languageSnapshotStore = useLanguageSnapshot();
      const { languageBlocks } = storeToRefs(languageSnapshotStore);

      return !languageBlocks.value?.length;
    }

    if (route.value?.name === 'usage') {
      const usageStore = useUsage();
      const { jobs, engagementUsers } = storeToRefs(usageStore);

      return !jobs.value?.length && !engagementUsers.value?.length;
    }

    if (['compliance-report', 'employer-brand-report', 'job-performance-report'].includes(route.value?.name)) {
      const actionableInsightsStore = useActionableInsights();
      const { documents } = storeToRefs(actionableInsightsStore);

      return !documents.value?.length;
    }

    if (route.value?.name === 'demographic-survey-report') {
      const demographicSurveyStore = useDemographicSurveyReport();
      const { isEmptyTabData } = storeToRefs(demographicSurveyStore);

      return isEmptyTabData.value;
    }

    if (route.value?.name === 'gender-report') {
      return useGenderReport().isEmptyData;
    }

    if (route.value?.name === 'pass-through' || route.value?.name === 'drop-off') {
      return usePassthroughDropoffReport().isEmptyData;
    }

    if (route.value?.name === 'hiring-report') {
      return useHiringReport().isEmptyData;
    }

    if (route.value?.name === 'overall-report') {
      return useOverallReport().isEmptyData;
    }

    if (route.value?.name === 'outbound-report') {
      return useOutboundReport().isEmptyData;
    }

    if (route.value?.name === 'inbound-report') {
      return useInboundReport().isEmptyData;
    }

    if (route.value?.name === 'executive-insights') {
      return useExecutiveInsights().isEmptyData;
    }

    return false;
  });

  // Synchronous Actions
  const setLabelsForLoading = (value) => {
    labelsForLoading.value = value;
  };

  const setReportingMessage = (message) => {
    reportingMessage.value = { ...message };
  };

  const updateEmails = ({ succeeded, failed }) => {
    emailStatus.value.failed = {
      ...emailStatus.value.failed,
      items: failed,
    };
    emailStatus.value.succeeded = {
      ...emailStatus.value.succeeded,
      items: succeeded,
    };
  };

  const setShowInternalDefault = (value) => {
    showInternalDefault.value = value;
  };

  // Asynchronous Actions
  const loadPage = async ({
    router,
    routeQuery,
    selectedFilters,
    selectedDateRange,
    page,
    sort,
    selectedMyJobs,
    searchTerm,
    selectedSubMenuOption,
    resetData,
  }) => {
    /* handle loading behaviors */
    useAppLoading().setLoading({
      pageLoading: true, filtersLoading: true, listLoading: true, halfTopBarLoading: true,
    });

    // Handle persisting and setting internal default
    if (showInternalDefault.value && [
      'compliance-report', 'employer-brand-report', 'job-performance-report', 'usage',
    ].includes(router.currentRoute.value.name)
    && !navigatedToDefaultExternalPageViaHistory && !routeQuery?.share) {
      // eslint-disable-next-line no-param-reassign
      if (!routeQuery || isEmpty(routeQuery)) routeQuery = { internal: 'external' };
      // eslint-disable-next-line no-param-reassign
      else routeQuery = { ...routeQuery, internal: routeQuery.internal || 'external' };

      showInternalDefault.value = false;
    } else if (!showInternalDefault.value && ![
      'compliance-report', 'employer-brand-report', 'job-performance-report', 'usage',
    ].includes(router.currentRoute.value.name)) {
      showInternalDefault.value = true;
    }

    navigatedToDefaultExternalPageViaHistory = false;

    /* reset search term */
    if (searchTerm) useFilters().setSearchTerm('');

    try {
      /* handle page specific requests and logic */
      if (router.currentRoute.value.name === 'usage') {
        await useUsage().getPlatformUsage({
          routeQuery,
          selectedDateRange,
          selectedFilters,
          page,
          router,
          selectedMyJobs,
        });
      }

      if ([
        'compliance-report', 'employer-brand-report', 'job-performance-report',
      ].includes(router.currentRoute.value.name)) {
        const actionableInsightTableLabels = {
          'compliance-report': 'Non-Compliant Open Jobs',
          'employer-brand-report': 'Easy Win Open Jobs',
          'job-performance-report': 'Open Jobs Missing Job Content',
        };

        const skeletonLoadersTimeout = setTimeout(() => {
          setLabelsForLoading({
            widgetLabels: [],
            tableLabel: actionableInsightTableLabels[router.currentRoute.value.name] || '',
          });
          // add intermediate loading state to filter bar
          useAppLoading().setLoading({ halfTopBarLoading: false });
        }, 4000);

        const {
          getEmployerBrandReport,
          getComplianceReport,
          getJobPerformanceReport,
        } = useActionableInsights();

        if (router.currentRoute.value.name === 'compliance-report') {
          await getComplianceReport({
            routeQuery,
            selectedFilters,
            page,
            sort: sort || selectedSubMenuOption || '-severity',
            router,
            selectedMyJobs,
            resetData,
          });
        }

        if (router.currentRoute.value.name === 'employer-brand-report') {
          await getEmployerBrandReport({
            routeQuery,
            selectedDateRange,
            selectedFilters,
            page,
            sort: sort || selectedSubMenuOption || '-severity',
            router,
            selectedMyJobs,
            resetData,
          });
        }

        if (router.currentRoute.value.name === 'job-performance-report') {
          await getJobPerformanceReport({
            routeQuery,
            selectedDateRange,
            selectedFilters,
            page,
            sort: sort || selectedSubMenuOption || '-severity',
            router,
            selectedMyJobs,
            resetData,
          });
        }

        clearTimeout(skeletonLoadersTimeout);
        setLabelsForLoading({});
      }
    } catch (error) {
      // Make sure we don't show our internal api information to the user
      const safeError = getSafeError(error);

      useAppSnackbar().addErrorSnackbar({
        text: `Unable to load ${startCase(router?.currentRoute?.value?.name || 'page')} - ${safeError}`,
      });

      throw error;
    } finally {
      useAppLoading().setLoading({
        pageLoading: false, filtersLoading: false, listLoading: false, halfTopBarLoading: false,
      });
    }
  };

  return {
    // State
    labelsForLoading,
    reportingMessage,
    emailStatus,
    showInternalDefault,

    // Getters
    emptyData,

    // Synchronous Actions
    setReportingMessage,

    updateEmails,
    setShowInternalDefault,

    // Asynchronous Actions
    loadPage,
  };
});
